/**
 * Centralized API Endpoint Constants
 * All backend API endpoints are defined here as constants
 * These are hardcoded values and do not depend on environment variables
 */

// ===========================
// Alert API Endpoints
// ===========================
export const ALERT_ENDPOINTS = {
  LIST: '/meta-data/alert-details/alert-cardlist',
  CLOSE: '/meta-data/alert-details/close-alert',
  HEAD: '/meta-data/alert-details/alertData?alertId=',
  DETAILS: '/meta-data/alert-details/alertData',
  DETAILS_V2: '/meta-data/alert-details/alertData-v2?alertId=',
  DATA_FLOW: '/meta-data/sequenceDetails',
};

// ===========================
// Release Management APIs
// ===========================
export const RELEASE_ENDPOINTS = {
  DASHBOARD: '/meta-data/release-management/release-dashboard',
  SUPPORT_DASHBOARD: '/meta-data/release-management/support-dashboard',
  LIST: '/meta-data/release-management/release-management-card-list',
  CALENDAR: '/meta-data/release-management/release-management-card-calendar',
  HEAD: '/meta-data/release-management/release-data?releaseId=',
  TEST_CASES: '/meta-data/release-management/q-metry-v1',
  TEST_CYCLES: '/meta-data/release-management/testcycle',
  PIVOT_REPORT: '/meta-data/release-management/q-metry-v1',
};

// ===========================
// Resource APIs
// ===========================
export const RESOURCE_ENDPOINTS = {
  LIST: '/meta-data/resource-cardlist',
  DETAILS: '/meta-data/resourcePageDetails?resourceId=',
  DETAILS_V2: '/meta-data/resourceDetails-v2?resourceId=',
  ALL_INFO: '/meta-data/getSrResourceData',
  DATA: '/meta-data/tableData',
  TABLE_DATA: '/tables/data',
  STATUS: '/meta-data/',
  ACTIVE: '/SR_RESOURCE_ACTIVE',
  FEATURE: 10,
};

// ===========================
// Admin APIs
// ===========================
export const ADMIN_ENDPOINTS = {
  ADD: '/meta-data/insertdata',
  UPDATE: '/meta-data/updatedata',
  DELETE: '/meta-data/multiDeleteById',
  GET_BY_TABLE: '/meta-data/tableData',
};

// ===========================
// QA Dashboard APIs
// ===========================
export const QA_DASHBOARD_ENDPOINTS = {
  SUMMARY: '/meta-data/qa-dashboard/summary',
  STORY_TESTING: '/meta-data/qa-dashboard/story-testing',
  PROJECTS_RELEASES: '/meta-data/qa-dashboard/ref/releases',
  JQL_RELEASES: '/meta-data/qa-dashboard/ref/jql-releases',
  FILTERS: '/meta-data/qa-dashboard/filters',
  SAVED_JQL_FILTERS: '/meta-data/qa-dashboard-saved-filter/getAllSavedFilters',
  TICKETS_BY_JQL_FILTER:
    '/meta-data/qa-dashboard-saved-filter/getTicketsByJqlFilterId',
  WORKFLOW: '/meta-data/qa-dashboard/workflow',
  QUALITY: '/meta-data/qa-dashboard/quality-section',
  REGRESSION: '/meta-data/qa-dashboard/regression-testing-section',
  SUMMARY_METRIC_DETAILS: '/meta-data/qa-dashboard/summary-metric-details',
  STORY_TESTING_DETAILS: '/meta-data/qa-dashboard/story-testing/details',
  REGRESSION_TESTING_DETAILS:
    '/meta-data/qa-dashboard/regression-testing/details',
  QUALITY_DETAILS: '/meta-data/qa-dashboard/quality-details',
  WORKFLOW_DETAILS: '/meta-data/qa-dashboard/workflow-details',

  // Summary Section - per-metric detail endpoints
  SUMMARY_STORIES_DONE: '/meta-data/qa-dashboard/summary/stories-done',
  SUMMARY_FIXES_DONE: '/meta-data/qa-dashboard/summary/fixes-done',
  SUMMARY_STORIES_COVERED: '/meta-data/qa-dashboard/summary/stories-covered',
  SUMMARY_STORY_TESTS_PASSED:
    '/meta-data/qa-dashboard/summary/story-tests-passed',
  SUMMARY_BUG_FIXES_COVERED: '/meta-data/qa-dashboard/summary/bugs-covered',
  SUMMARY_BUG_TESTS_PASSED: '/meta-data/qa-dashboard/summary/bug-tests-passed',
  SUMMARY_BUGS: '/meta-data/qa-dashboard/summary/bugs',
  SUMMARY_REGRESSION_TESTS: '/meta-data/qa-dashboard/summary/regression-tests',

  // Quality Section - per-status detail endpoints
  QUALITY_PASSED: '/meta-data/qa-dashboard/quality-section/passed',
  QUALITY_FAILED: '/meta-data/qa-dashboard/quality-section/failed',
  QUALITY_NOT_EXECUTED: '/meta-data/qa-dashboard/quality-section/not-executed',
  QUALITY_IN_PROGRESS: '/meta-data/qa-dashboard/quality-section/in-progress',
  QUALITY_BLOCKED: '/meta-data/qa-dashboard/quality-section/blocked',
  QUALITY_MISSING: '/meta-data/qa-dashboard/quality-section/missing',
  QUALITY_ALL: '/meta-data/qa-dashboard/quality-section/all',

  // Quality Section - per-metric bug detail endpoints
  // All Quality bug drill-downs (openBugs, foundByQA, escapedCriticalBugs,
  // openBugsBySeverity, bugAging) are now served by the consolidated
  // QUALITY_DETAILS endpoint above, distinguished by the `metricType` field.
};

// ===========================
// Regression Dashboard APIs
// ===========================
export const REGRESSION_DASHBOARD_ENDPOINTS = {
  PROJECTS: '/meta-data/regression-dashboard/ref/projects',
  FILTER_OPTIONS: '/meta-data/regression-dashboard/ref/filter-options',
  SUMMARY: '/meta-data/regression-dashboard/summary',
  COVERAGE_GROWTH: '/meta-data/regression-dashboard/coverage-growth',
  TEST_TYPE_MIX: '/meta-data/regression-dashboard/coverage/test-type-mix',
  COVERAGE_BY_APPLICATION:
    '/meta-data/regression-dashboard/coverage-by-application',
  COVERAGE_BY_INTEGRATION:
    '/meta-data/regression-dashboard/coverage-by-integration',
  COVERAGE_BY_FOLDER: '/meta-data/regression-dashboard/coverage-by-folder',
  COVERAGE_BY_EPIC: '/meta-data/regression-dashboard/coverage-by-epic',
};

// ===========================
// Template APIs (AG Grid)
// ===========================
export const TEMPLATE_ENDPOINTS = {
  GET: '/meta-data/template-details/getTemplateByFeatureId',
  CREATE: '/meta-data/template-details/createTemplate',
  UPDATE: '/meta-data/template-details/updateTemplate',
  DELETE: '/meta-data/template-details/deleteTemplate',
};

// ===========================
// Test Case APIs
// ===========================
export const TEST_CASE_ENDPOINTS = {
  STEPS: '/meta-data/test-step/step-details',
  JIRA_PROJECTS: '/meta-data/project-management/flagged-projects',
};

// ===========================
// Misc MetaData APIs
// ===========================
export const METADATA_ENDPOINTS = {
  TABLES: '/meta-data/desiredTableNames',
  USERS: '/meta-data/',
  SOP_DETAILS: '/meta-data/sopDetails?sopId=',
  FILTER_DATA: '/meta-data/search/data',
};

// ===========================
// Agent API Base Paths
// ===========================
export const AGENT_API_PATHS = {
  RELEASE_ANALYZER: '/release-agent',
  TEST_CASE_CREATOR: '/test-case-creator',
  KNOWLEDGE_BASE: '/knowledge-base',
};

// ===========================
// Authentication Endpoints
// ===========================
export const AUTH_ENDPOINTS = {
  // Note: AUTH_ENDPOINT is still in .env as it varies by environment
  // Use: import.meta.env.VITE_AUTH_ENDPOINT
  GENERATE_TOKEN: '/auth-service/auth-details/generateTokenForUser',
};

// ===========================
// Combined Export (for convenience)
// ===========================
export const API_ENDPOINTS = {
  ALERT: ALERT_ENDPOINTS,
  RELEASE: RELEASE_ENDPOINTS,
  RESOURCE: RESOURCE_ENDPOINTS,
  ADMIN: ADMIN_ENDPOINTS,
  QA_DASHBOARD: QA_DASHBOARD_ENDPOINTS,
  TEMPLATE: TEMPLATE_ENDPOINTS,
  TEST_CASE: TEST_CASE_ENDPOINTS,
  METADATA: METADATA_ENDPOINTS,
  AGENT: AGENT_API_PATHS,
  AUTH: AUTH_ENDPOINTS,
};

// ===========================
// Helper Functions
// ===========================

/**
 * Build alert details endpoint with alertId
 * @param {string} alertId - Alert ID
 * @return {string} Complete endpoint URL
 */
export const buildAlertDetailsEndpoint = (alertId) =>
  `${ALERT_ENDPOINTS.HEAD}${alertId}`;

/**
 * Build release details endpoint with releaseId
 * @param {string} releaseId - Release ID
 * @return {string} Complete endpoint URL
 */
export const buildReleaseDetailsEndpoint = (releaseId) =>
  `${RELEASE_ENDPOINTS.HEAD}${releaseId}`;

/**
 * Build resource details endpoint with resourceId
 * @param {string} resourceId - Resource ID
 * @return {string} Complete endpoint URL
 */
export const buildResourceDetailsEndpoint = (resourceId) =>
  `${RESOURCE_ENDPOINTS.DETAILS}${resourceId}`;

/**
 * Build resource details V2 endpoint with resourceId
 * @param {string} resourceId - Resource ID
 * @return {string} Complete endpoint URL
 */
export const buildResourceDetailsV2Endpoint = (resourceId) =>
  `${RESOURCE_ENDPOINTS.DETAILS_V2}${resourceId}`;

/**
 * Build filter data endpoint with page parameter
 * @param {string} page - Page name (admin, release, resource)
 * @return {string} Complete endpoint URL
 */
export const buildFilterDataEndpoint = (page) =>
  `${METADATA_ENDPOINTS.FILTER_DATA}?page=${page}`;

/**
 * Build template GET endpoint with featureId
 * @param {string} featureId - Feature ID
 * @return {string} Complete endpoint URL
 */
export const buildTemplateGetEndpoint = (featureId) =>
  `${TEMPLATE_ENDPOINTS.GET}/${featureId}`;

/**
 * Build template DELETE endpoint with templateId
 * @param {string} templateId - Template ID
 * @return {string} Complete endpoint URL
 */
export const buildTemplateDeleteEndpoint = (templateId) =>
  `${TEMPLATE_ENDPOINTS.DELETE}/${templateId}`;

/**
 * Build test case steps endpoint with testCaseKey and versionNumber
 * @param {string} testCaseId - Test Case ID
 * @param {string} versionNo - Version Number
 * @return {string} Complete endpoint URL
 */
export const buildTestCaseStepsEndpoint = (testCaseId, versionNo) =>
  `${TEST_CASE_ENDPOINTS.STEPS}?testCaseKey=${testCaseId}&versionNumber=${versionNo}`;

/**
 * Build admin endpoint with table name
 * @param {string} baseEndpoint - Base endpoint (ADD, UPDATE, DELETE, GET_BY_TABLE)
 * @param {string} tableName - Table name
 * @return {string} Complete endpoint URL
 */
export const buildAdminEndpoint = (baseEndpoint, tableName) =>
  `${baseEndpoint}/${tableName}`;
